import { CollapsibleSectionWithListener } from '@src/common/components/CollapsibleSectionWithListener';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
} from '@src/common/components/Table';
import { TableRow } from '@src/common/components/TableRow';
import type { PermitStatus } from '@src/orders/domain/PermitApplication';
import { duplicateOrderEvents } from '@src/orders/DuplicateOrderEvents';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type PermitRowData = {
  id: string;
  name: string;
  type: string;
  jurisdiction: string;
  status: string;
};

export interface PermitSelectionColumns {
  field: string;
  headerName: string;
  width?: number;
  render?: (row: PermitSelectionRowsData) => ReactNode;
}

export interface PermitSelectionRowsData {
  name: string;
  id: string;
  type: string;
  jurisdiction: string;
  status: PermitStatus;
  permitTypeIsActive?: boolean;
}

export type Props = {
  columns: PermitSelectionColumns[];
  rows: PermitSelectionRowsData[];
};

export const PermitSelectionSection = ({ columns, rows }: Props) => {
  const { t } = useTranslation();

  return (
    <CollapsibleSectionWithListener
      title={t('permits')}
      subtitle={t('selectPermitsInstructions')}
      collapseEventName={duplicateOrderEvents.collapseEventName}
      uncollapseEventName={duplicateOrderEvents.uncollapseEventName}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: 'white' }}>
            {columns.map((column) => (
              <TableCell key={column.field} style={{ width: column.width }}>
                {column.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              {columns.map((column) => (
                <TableCell key={`${row.id}-${column.field}`}>
                  {column.render
                    ? column.render(row)
                    : row[column.field as keyof PermitRowData]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CollapsibleSectionWithListener>
  );
};
